import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, AlertTriangle, BadgeIndianRupee } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const MACRO =
  "https://static.prod-images.emergentagent.com/jobs/7076c684-c2dc-44d5-b819-1abf0dd5cbeb/images/d0b3ca8054526920a985dc4155557eb0b13d1b89d310f48a9f022020c6d1a5e8.png";

const RZP_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.querySelector(`script[src="${RZP_SCRIPT_SRC}"]`)) {
      resolve(true);
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    loadRazorpayScript();
  }, []);

  const handlePay = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Could not load Razorpay SDK.");
        return;
      }
      const { data } = await api.post("/payments/order", { amount_inr: 1 });
      setOrder(data);

      if (data.mock_mode) {
        toast.warning("Razorpay mock_mode — placeholder keys in use.");
      }

      const opts = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "JewelleryOnWheels",
        description: "Phase 0 POC — Pay ₹1 test charge",
        image:
          "https://customer-assets.emergentagent.com/job_c129d2ac-2209-4214-9ac4-af6d02f0e7d1/artifacts/e10f2m7m_hf_20260429_103436_b903d0ed-1980-49ff-95c4-cf93c75881ec.png",
        order_id: data.order_id,
        prefill: { name: "Demo Customer", email: "demo@jewelleryonwheels.in" },
        theme: { color: "#0A1F44" },
        handler: async (resp) => {
          try {
            const verifyRes = await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setLastResult({ kind: "verify", ...verifyRes.data });
            if (verifyRes.data.verified) {
              toast.success("Signature verified — payment authentic.");
            } else {
              toast.error("Signature verification failed.");
            }
          } catch (err) {
            toast.error("Verification request failed.");
          }
        },
        modal: {
          ondismiss: () => {
            setLastResult({ kind: "dismissed" });
            toast.info("Razorpay modal closed.");
          },
        },
      };

      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", (resp) => {
        setLastResult({ kind: "failed", error: resp.error });
        toast.error(`Payment failed: ${resp.error?.description || "unknown"}`);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create order.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 space-y-10"
        >
          <div>
            <span className="label-overline text-jow-gold">Phase 0 · Razorpay test mode</span>
            <h1 className="font-display text-4xl md:text-5xl mt-3 text-jow-navy tracking-tight">
              A ceremonial ₹1 charge.
            </h1>
            <p className="mt-4 text-jow-navy/65 max-w-xl">
              Click below to load the Razorpay checkout. With placeholder keys the
              modal still opens in <em>mock mode</em> — set real test keys in your
              backend <code className="text-jow-pink">.env</code> to take a real
              ₹1 test payment.
            </p>
          </div>

          <div className="border border-jow-gold/40 bg-white p-8 md:p-10">
            <div className="flex items-baseline justify-between mb-8">
              <div>
                <p className="label-overline">Test charge</p>
                <p className="font-display text-5xl mt-2 text-jow-navy flex items-center gap-1">
                  <BadgeIndianRupee size={36} className="text-jow-gold" />1
                  <span className="text-base text-jow-navy/50 font-sans tracking-widest ml-3">
                    INR
                  </span>
                </p>
              </div>
              <div className="text-right text-xs text-jow-navy/60">
                <p>Razorpay</p>
                <p className="mt-1 font-mono">rzp_test_…</p>
              </div>
            </div>

            <button
              onClick={handlePay}
              disabled={loading}
              className="btn-primary-jow w-full"
              data-testid="pay-button"
            >
              <CreditCard size={16} />
              {loading ? "Preparing checkout..." : "Pay ₹1"}
            </button>

            {order?.mock_mode && (
              <div
                className="mt-6 flex items-start gap-3 border border-jow-pink/30 bg-jow-pink/5 px-4 py-3 text-sm text-jow-navy"
                data-testid="mock-mode-banner"
              >
                <AlertTriangle size={16} className="text-jow-pink mt-0.5" />
                <span>
                  Razorpay using placeholder keys — modal will load but payment will
                  fail until real test keys are set in
                  <code className="text-jow-pink ml-1">backend/.env</code>.
                </span>
              </div>
            )}
          </div>

          {order && (
            <div className="border border-jow-navy/10 bg-jow-cream p-6" data-testid="order-summary">
              <p className="label-overline">Order created</p>
              <pre className="mt-3 text-xs leading-relaxed font-mono text-jow-navy/80 whitespace-pre-wrap break-all">
                {JSON.stringify(order, null, 2)}
              </pre>
            </div>
          )}

          {lastResult && (
            <div className="border border-jow-navy/10 bg-white p-6" data-testid="last-result">
              <p className="label-overline">Last response</p>
              <pre className="mt-3 text-xs leading-relaxed font-mono text-jow-navy/80 whitespace-pre-wrap break-all">
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            </div>
          )}
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="aspect-[4/5] overflow-hidden border border-jow-gold/40">
            <img src={MACRO} alt="Premium kundan jewellery" className="w-full h-full object-cover" />
          </div>
          <p className="mt-4 label-overline text-jow-navy/60">
            Visual placeholder · representative product
          </p>
        </motion.aside>
      </div>
    </div>
  );
}
